import { App } from "@capacitor/app"
import { CapacitorUpdater } from "@capgo/capacitor-updater"
import { markCapgoStartupComplete } from "./mobile_app/services/capgo-update-service"
import { markNativeStoreStartupComplete } from "./mobile_app/services/native-store-update-service"
import { initializeStatusBar } from "./mobile_app/services/statusbar-service"
import { storeToRefs } from "pinia"
import { useWorkPeriodService } from "./mobile_app/services/work-period-service"
import { useAuthStore } from "./store/auth.store"
import { SplashScreen } from "@capacitor/splash-screen"
import { useLayoutColumnsStore } from "./store/layout-columns.store"
import { watch } from "vue"
import { getToken } from "@clerk/vue"
import { cleanupSendIntentFiles } from "./mobile_app/services/send-intent-file-service"

function preBootMobileApp() {
  CapacitorUpdater.notifyAppReady()
  void cleanupSendIntentFiles()
  void App.addListener("appStateChange", ({ isActive }) => {
    if (isActive) void cleanupSendIntentFiles()
  })
  //some logic for showing/hiding splash screen should be here
}

// Maximum time to keep the splash screen up while waiting for the authenticated
// Django user to resolve. Without this fallback, a user that has a Clerk token but
// no knownBusinessEntityContact (or a failed auth sync) leaves the app stuck on the
// immersive splash screen indefinitely, with no visible error and a login/error
// dialog hidden behind it.
const SPLASH_FALLBACK_TIMEOUT_MS = 8000

async function postBootMobileApp() {
  const { allColumnsLoaded } = storeToRefs(useLayoutColumnsStore())
  const authStore = useAuthStore()
  const { activeUser } = storeToRefs(authStore)

  try {
    const token = await getToken({ template: "jwt" })

    // Wait for both conditions to be true before proceeding
    if (token && !activeUser.value?.knownBusinessEntityContact) {
      // Create a promise that resolves when both conditions are met, or when the
      // fallback timeout elapses, so the splash can never hang the app.
      await new Promise<void>((resolve) => {
        let unwatch: () => void = () => {}

        const timeout = setTimeout(() => {
          unwatch()
          resolve()
        }, SPLASH_FALLBACK_TIMEOUT_MS)

        unwatch = watch(
          [activeUser, allColumnsLoaded],
          async () => {
            //this is to avoid flickering between allcolumnsloaded true and false.
            await new Promise((resolve) => setTimeout(resolve, 1000))

            if (activeUser.value?.knownBusinessEntityContact) {
              //await splash screen hide one second to avoid flickering
              clearTimeout(timeout)
              unwatch() // Stop watching once conditions are met
              resolve() // Resolve the promise to continue execution
            }
          },
          { immediate: true }
        )
      })
    }
  } catch (e) {
    console.error("postBootMobileApp: failed while resolving authenticated user", e)
  } finally {
    // Always hide the splash so the app (or a login/error dialog) becomes visible.
    await SplashScreen.hide()
    markCapgoStartupComplete()
    markNativeStoreStartupComplete()
  }

  // Add a 500ms delay before initializing status bar
  initializeStatusBar(activeUser.value != null)

  //temp fix to make compiler happy
  if (!activeUser.value?.knownBusinessEntityContact) {
    return
  }

  const { checkAndResumeActiveWorkPeriod } = useWorkPeriodService()

  await checkAndResumeActiveWorkPeriod(activeUser.value.knownBusinessEntityContact)
}

// Add a watcher to run postBootMobileApp when activeUser changes
function setupActiveUserWatcher() {
  const authStore = useAuthStore()
  const { activeUser } = storeToRefs(authStore)

  watch(activeUser, () => {
    if (activeUser.value?.knownBusinessEntityContact) {
      postBootMobileApp()
    }
  })
}

export { preBootMobileApp, postBootMobileApp, setupActiveUserWatcher }
